import { attributes } from '../../core/ids-attributes';
import { IdsConstructor } from '../../core/ids-element';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import type { IdsLabelStateMode } from './ids-label-state-common';
import { IdsLabelStateAttributes, isLabelStateValid, isLabelRequiredValid } from './ids-label-state-common';
import { LabelStateHandler } from './ids-label-state-mixin';

export interface InputField {
  labelWrap?: string | 'ellipsis' | 'wrap' | 'ellipsis-no-stretch' | 'wrap-no-stretch';
}

interface LabelStateParentHandler {
  onLabelChange?(label: string | null): void;
  onLabelRequiredChange?(): void;
  onLabelWrapChange?(): void;
  input?: InputField | null;
  triggerField?: InputField | null;
  labelWrap?: string | null;
}

type Constraints = IdsConstructor<LabelStateParentHandler & LabelStateHandler>;

/**
 * A mixin that will pass down Label State features to an IdsInput/IdsTriggerField inside another component's shadow root
 * @param {any} superclass Accepts a superclass and creates a new subclass from it
 * @returns {any} The extended object
 */
const IdsLabelStateParentMixin = <T extends Constraints>(superclass: T) => class extends superclass {
  constructor(...args: any[]) {
    super(...args);
  }

  connectedCallback() {
    super.connectedCallback?.();
    if (this.hasAttribute(attributes.LABEL_STATE)) {
      this.labelState = this.getAttribute(attributes.LABEL_STATE) as IdsLabelStateMode;
    }
    // The inner field can be re-rendered/re-shadowed after this callback (e.g. via a
    // deferred render), so reconcile once the field has settled to guarantee the hidden
    // label value reaches the accessor and syncs `aria-label`.
    requestAnimationFrame(() => this.#reapplyInnerLabelProps());
  }

  static get attributes() {
    return [
      ...(superclass as any).attributes,
      ...IdsLabelStateAttributes
    ];
  }

  /**
   * Set the `label` text
   * @param {string} value of the `label` text property
   */
  set label(value: string) {
    if (value !== null) {
      this.setAttribute(attributes.LABEL, value);
    } else {
      this.removeAttribute(attributes.LABEL);
    }
    if (typeof this.onLabelChange === 'function') this.onLabelChange(value);
    this.#reapplyInnerLabelProps();
  }

  get label(): string {
    return this.getAttribute(attributes.LABEL) ?? '';
  }

  /**
   * Sets the `label-state` attribute on the inner IdsDropDrown
   */
  set labelState(value: IdsLabelStateMode) {
    if (isLabelStateValid(value)) {
      if (value !== null) {
        this.setAttribute(attributes.LABEL_STATE, value);
      } else {
        this.removeAttribute(attributes.LABEL_STATE);
      }
      if (typeof this.onLabelStateChange === 'function') this.onLabelStateChange(value);
      this.updateLabelWrapForCollapsedState();
      this.#reapplyInnerLabelProps();
    }
  }

  get labelState(): IdsLabelStateMode {
    return this.getAttribute(attributes.LABEL_STATE) as IdsLabelStateMode;
  }

  /**
   * Set `label-required` attribute
   * @param {string} value The `label-required` attribute
   */
  set labelRequired(value: string | boolean) {
    const isValid = isLabelRequiredValid(value);
    if (isValid) {
      this.setAttribute(attributes.LABEL_REQUIRED, isValid.toString());
    } else {
      this.setAttribute(attributes.LABEL_REQUIRED, 'false');
    }
    if (typeof this.onLabelRequiredChange === 'function') this.onLabelRequiredChange();
  }

  get labelRequired(): boolean {
    const value = this.getAttribute(attributes.LABEL_REQUIRED);
    return value !== null ? stringToBool(value) : true;
  }

  /**
   * Update label wrap based on collapsed state
   * @returns {void}
   */
  updateLabelWrapForCollapsedState(): void {
    const inputField = this.input || this.triggerField;
    if (inputField && 'labelWrap' in inputField) {
      inputField.labelWrap = (this.labelState && this.labelState === 'collapsed') ? 'wrap' : (this.labelWrap || 'wrap');
    }
  }

  /**
   * Reapplies the current `label`/`label-state` values to the inner field through its
   * prototype accessors. When a composite component renders its inner field in shadow DOM,
   * the field can still be an un-upgraded `HTMLElement` while the parent's `onLabelChange`/
   * `onLabelStateChange` handlers run. Assigning `label`/`labelState` at that point creates a
   * plain own property that permanently shadows the mixin's accessor, so later updates never
   * reach the setter that keeps `aria-label` in sync while the label is hidden/collapsed.
   * Deleting the shadowing own property and re-assigning routes the value through the accessor.
   * @returns {void}
   */
  #reapplyInnerLabelProps(): void {
    const inputField = (this.input || this.triggerField) as any;
    if (!inputField) return;

    (['label', 'labelState'] as const).forEach((prop) => {
      if (!Object.prototype.hasOwnProperty.call(inputField, prop)) return;

      // Confirm an accessor exists on the prototype chain before de-shadowing
      let proto = Object.getPrototypeOf(inputField);
      let hasAccessor = false;
      while (proto) {
        const descriptor = Object.getOwnPropertyDescriptor(proto, prop);
        if (descriptor) {
          hasAccessor = typeof descriptor.set === 'function';
          break;
        }
        proto = Object.getPrototypeOf(proto);
      }
      if (!hasAccessor) return;

      delete inputField[prop];
      inputField[prop] = prop === 'label' ? this.label : this.labelState;
    });
  }
};

export default IdsLabelStateParentMixin;
